import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import {
  MessageSquare, ChevronUp, ChevronDown, Filter, RefreshCw,
  CheckCircle, XCircle, Clock, AlertTriangle, Loader2, Send, Trash2,
} from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useAdminLogBulkDelete } from '../hooks/useAdminLogBulkDelete';
import { DateInput } from '../components/DateInput';

interface SmsLog {
  id: string;
  to: string;
  body: string;
  templateType: string | null;
  correlationId: string | null;
  status: string;
  providerMessageId: string | null;
  providerResponse: string | null;
  errorMessage: string | null;
  segments: number | null;
  clientId: string | null;
  appointmentId: string | null;
  client?: { id: string; firstName: string; lastName: string } | null;
  appointment?: { id: string; scheduledAt: string } | null;
  sentAt: string | null;
  createdAt: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  SENT: { label: 'Envoyé', color: 'text-green-600 bg-green-50', icon: CheckCircle },
  DELIVERED: { label: 'Délivré', color: 'text-green-700 bg-green-100', icon: CheckCircle },
  PENDING: { label: 'En attente', color: 'text-amber-600 bg-amber-50', icon: Clock },
  FAILED: { label: 'Échoué', color: 'text-red-600 bg-red-50', icon: XCircle },
  REJECTED: { label: 'Rejeté', color: 'text-red-700 bg-red-100', icon: AlertTriangle },
};

export function SmsLogsPage() {
  const { t } = useTranslation();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage();
  const [items, setItems] = useState<SmsLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedLog, setSelectedLog] = useState<SmsLog | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    to: '',
    templateType: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    limit: '50',
    offset: '0',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const loadLogs = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await apiClient(`/api/admin/sms-logs?${params}`);

      if (!response.ok) throw new Error('Failed to load SMS logs');

      const data = await response.json();
      if (data.success) {
        setItems(data.data.items || []);
        setTotal(data.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load SMS logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, canLoad]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs, currentShopId]);

  const visibleIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    selectedArray,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const bulkDelete = useAdminLogBulkDelete({
    softDeletePath: '/api/admin/sms-logs/bulk-soft-delete',
    hardDeletePath: '/api/admin/sms-logs/bulk-hard-delete',
    i18n: {
      error: 'admin.smsLogs.bulkDelete.error',
      partialSoft: 'admin.smsLogs.bulkDelete.partialSoft',
      partialHard: 'admin.smsLogs.bulkDelete.partialHard',
      softSuccess: 'admin.smsLogs.bulkDelete.softSuccess',
      hardSuccess: 'admin.smsLogs.bulkDelete.hardSuccess',
      softTitle: 'admin.smsLogs.bulkDelete.softTitle',
      softMessage: 'admin.smsLogs.bulkDelete.softMessage',
      entityLabel: 'admin.smsLogs.bulkDelete.entityLabel',
    },
    onComplete: async () => {
      clearSelection();
      await loadLogs();
    },
  });

  const resendSms = async (logId: string) => {
    setResending(logId);
    try {
      const res = await apiClient(`/api/admin/sms-logs/${logId}/resend`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        loadLogs();
      }
    } catch (err) {
      console.error('Failed to resend SMS:', err);
    } finally {
      setResending(null);
    }
  };

  const handleSort = (field: string) => {
    setFilters({
      ...filters,
      sortBy: field,
      sortOrder: filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc',
      offset: '0',
    });
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline" />
    ) : (
      <ChevronDown className="w-3 h-3 inline" />
    );
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const currentPage = Math.floor(Number(filters.offset) / Number(filters.limit)) + 1;
  const totalPages = Math.ceil(total / Number(filters.limit));

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="dt-list-toolbar flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Historique SMS</h1>
            <p className="text-sm text-textSecondary">{total} message(s) au total</p>
          </div>
        </div>
        <button onClick={loadLogs} className="btn-secondary flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <span className="flex items-center gap-2 font-medium text-textPrimary">
            <Filter className="w-4 h-4" /> Filtres
          </span>
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showFilters && (
          <div className="dt-filters px-4 pb-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <input
              type="text"
              placeholder="Numéro de téléphone"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value, offset: '0' })}
              className="input-field"
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, offset: '0' })}
              className="input-field"
            >
              <option value="">Tous les statuts</option>
              <option value="SENT">Envoyé</option>
              <option value="DELIVERED">Délivré</option>
              <option value="PENDING">En attente</option>
              <option value="FAILED">Échoué</option>
              <option value="REJECTED">Rejeté</option>
            </select>
            <select
              value={filters.templateType}
              onChange={(e) => setFilters({ ...filters, templateType: e.target.value, offset: '0' })}
              className="input-field"
            >
              <option value="">Tous les types</option>
              <option value="appointment_confirmation">Confirmation</option>
              <option value="reminder_d7">Rappel J-7</option>
              <option value="reminder_d1">Rappel J-1</option>
              <option value="reminder_h2">Rappel H-2</option>
              <option value="appointment_cancelled">Annulation</option>
              <option value="appointment_rescheduled">Reprogrammation</option>
              <option value="sms_otp">OTP</option>
              <option value="test">Test</option>
            </select>
            <DateInput
              value={filters.dateFrom}
              onChange={(value) => setFilters({ ...filters, dateFrom: value, offset: '0' })}
              className="input-field"
              placeholder="Date début"
            />
            <DateInput
              value={filters.dateTo}
              onChange={(value) => setFilters({ ...filters, dateTo: value, offset: '0' })}
              className="input-field"
              placeholder="Date fin"
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {selectedCount > 0 && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap justify-between items-center gap-3">
            <span className="text-sm text-primary-600 font-medium">
              {t('admin.smsLogs.bulkDelete.selected', { count: selectedCount })}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => bulkDelete.setSoftDeleteModalOpen(true)}
                disabled={bulkDelete.deleteLoading}
                className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('admin.smsLogs.bulkDelete.soft', { count: selectedCount })}
              </button>
              {bulkDelete.canHardDelete && (
                <button
                  type="button"
                  onClick={() => bulkDelete.setHardDeleteModalOpen(true)}
                  disabled={bulkDelete.deleteLoading}
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('admin.smsLogs.bulkDelete.hard', { count: selectedCount })}
                </button>
              )}
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-textSecondary">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun SMS trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={items.length === 0}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('admin.smsLogs.bulkDelete.selectAll')}
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary cursor-pointer" onClick={() => handleSort('createdAt')}>
                    Date <SortIcon field="createdAt" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary cursor-pointer" onClick={() => handleSort('to')}>
                    Destinataire <SortIcon field="to" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary">Client</th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary cursor-pointer" onClick={() => handleSort('templateType')}>
                    Type <SortIcon field="templateType" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary cursor-pointer" onClick={() => handleSort('status')}>
                    Statut <SortIcon field="status" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary">Segments</th>
                  <th className="text-left px-4 py-3 font-medium text-textSecondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((log) => {
                  const statusCfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.PENDING;
                  const StatusIcon = statusCfg.icon;
                  return (
                    <tr
                      key={log.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedArray.includes(log.id) ? 'bg-primary-50/40' : ''}`}
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedArray.includes(log.id)}
                          onChange={() => toggleSelect(log.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          aria-label={t('admin.smsLogs.bulkDelete.selectRow')}
                        />
                      </td>
                      <td className="px-4 py-3 text-textSecondary whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{log.to}</td>
                      <td className="px-4 py-3">
                        {log.client ? (
                          <span className="text-textPrimary">
                            {log.client.firstName} {log.client.lastName}
                          </span>
                        ) : (
                          <span className="text-textSecondary">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          {log.templateType || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${statusCfg.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{log.segments ?? '-'}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {log.status === 'FAILED' && (
                          <button
                            onClick={() => resendSms(log.id)}
                            disabled={resending === log.id}
                            className="text-primary hover:text-primary/80 flex items-center gap-1 text-xs"
                          >
                            {resending === log.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3" />
                            )}
                            Renvoyer
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="dt-list-toolbar flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-textSecondary">
              Page {currentPage} / {totalPages} ({total} résultats)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, offset: String(Math.max(0, Number(filters.offset) - Number(filters.limit))) })}
                disabled={currentPage <= 1}
                className="btn-secondary text-sm py-1 px-3 disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setFilters({ ...filters, offset: String(Number(filters.offset) + Number(filters.limit)) })}
                disabled={currentPage >= totalPages}
                className="btn-secondary text-sm py-1 px-3 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-textPrimary">Détail du SMS</h2>
                <button onClick={() => setSelectedLog(null)} className="text-textSecondary hover:text-textPrimary">
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-textSecondary uppercase">Destinataire</label>
                  <p className="font-mono">{selectedLog.to}</p>
                </div>

                {selectedLog.client && (
                  <div>
                    <label className="text-xs font-medium text-textSecondary uppercase">Client</label>
                    <p>{selectedLog.client.firstName} {selectedLog.client.lastName}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-textSecondary uppercase">Message</label>
                  <p className="bg-gray-50 rounded p-3 text-sm">{selectedLog.body}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-textSecondary uppercase">Statut</label>
                    <p>{STATUS_CONFIG[selectedLog.status]?.label || selectedLog.status}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-textSecondary uppercase">Segments</label>
                    <p>{selectedLog.segments ?? '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-textSecondary uppercase">Type</label>
                    <p>{selectedLog.templateType || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-textSecondary uppercase">Provider ID</label>
                    <p className="font-mono text-xs">{selectedLog.providerMessageId || '-'}</p>
                  </div>
                </div>

                {selectedLog.errorMessage && (
                  <div>
                    <label className="text-xs font-medium text-red-600 uppercase">Erreur</label>
                    <p className="bg-red-50 rounded p-3 text-sm text-red-700">{selectedLog.errorMessage}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-textSecondary uppercase">Date de création</label>
                  <p>{formatDate(selectedLog.createdAt)}</p>
                </div>

                {selectedLog.status === 'FAILED' && (
                  <button
                    onClick={() => {
                      resendSms(selectedLog.id);
                      setSelectedLog(null);
                    }}
                    className="btn-primary flex items-center gap-2 w-full justify-center"
                  >
                    <Send className="w-4 h-4" />
                    Renvoyer ce SMS
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkDelete.toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm text-white ${
            bulkDelete.toast.type === 'success'
              ? 'bg-green-600'
              : bulkDelete.toast.type === 'warning'
                ? 'bg-amber-600'
                : 'bg-red-600'
          }`}
        >
          {bulkDelete.toast.message}
        </div>
      )}

      <ConfirmModal
        isOpen={bulkDelete.softDeleteModalOpen}
        title={t(bulkDelete.i18n.softTitle)}
        message={t(bulkDelete.i18n.softMessage, { count: selectedCount })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="warning"
        isLoading={bulkDelete.deleteLoading}
        onConfirm={() => bulkDelete.confirmBulkSoftDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setSoftDeleteModalOpen(false)}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkDelete.hardDeleteModalOpen}
        onConfirm={() => bulkDelete.confirmBulkHardDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setHardDeleteModalOpen(false)}
        entityLabel={t(bulkDelete.i18n.entityLabel)}
        count={selectedCount}
        isLoading={bulkDelete.deleteLoading}
      />
    </div>
  );
}
